import Grid from '@amzn/awsui-components-core/grid';
import Icon from '@amzn/awsui-components-core/icon';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import 'src/styles/dfp-home-page/landing-page.scss';
import DueThisWeekSection from './due-this-week/DueThisWeekSection';
import { FooterQuote } from './FooterQuote';
import QuickActionsSection from './QuickActionsSection';
import QuickLinksPanel from './QuickLinksPanel';
import RecentsFeaturedSection from './RecentsFeaturedSection';
import TestimonialsSection from './TestimonialsSection';
import WelcomeBanner from './WelcomeBanner';

type ScreenSize = 'small' | 'medium' | 'large';

const getScreenSize = (): ScreenSize => {
  const width = window.innerWidth;
  if (width <= 768) return 'small';
  if (width <= 1200) return 'medium';
  return 'large';
};

/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Main landing page container — composes all sections per the Figma design.
 * 1. Welcome Banner (greeting + promo)
 * 2. Quick Actions
 * 3. Recents / Featured
 * 4. Due This Week (finance calendar)
 * 5. Testimonials (carousel)
 * 6. Footer Quote (re-used from existing component)
 *
 * Sidebar behavior by screen size:
 * - Large (>1200px): sidebar visible by default, collapsible via toggle
 * - Medium (769–1200px): sidebar collapsed by default, expandable via toggle
 * - Small (≤768px): sidebar hidden, accessible via hamburger FAB (slide-out drawer)
 */
const LandingPage: React.FC = () => {
  const [screenSize, setScreenSize] = useState<ScreenSize>(getScreenSize);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const size = getScreenSize();
      setScreenSize(size);
      if (size === 'small') setMobileSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Medium screens default to collapsed
  useEffect(() => {
    setSidebarCollapsed(screenSize === 'medium');
  }, [screenSize]);

  const toggleDesktopSidebar = useCallback(() => setSidebarCollapsed((prev) => !prev), []);
  const toggleMobileSidebar = useCallback(() => setMobileSidebarOpen((prev) => !prev), []);
  const closeMobileSidebar = useCallback(() => setMobileSidebarOpen(false), []);

  const isSmall = screenSize === 'small';
  const showDesktopSidebar = !isSmall;

  return (
    <div className={`landing-page${sidebarCollapsed && !isSmall ? ' landing-page--sidebar-collapsed' : ''}`}>
      {/* Small screens: hamburger FAB + backdrop + slide-out drawer */}
      {isSmall &&
        createPortal(
          <button
            className="landing-page__hamburger"
            onClick={toggleMobileSidebar}
            aria-label={mobileSidebarOpen ? 'Close navigation' : 'Open navigation'}
          >
            {mobileSidebarOpen ? '✕' : '☰'}
          </button>,
          document.body
        )}
      {isSmall && mobileSidebarOpen && createPortal(<div className="landing-page__backdrop" onClick={closeMobileSidebar} />, document.body)}
      {isSmall &&
        createPortal(
          <aside className={`landing-page__sidebar landing-page__sidebar--mobile ${mobileSidebarOpen ? 'landing-page__sidebar--open' : ''}`}>
            <QuickLinksPanel />
          </aside>,
          document.body
        )}

      {/* Medium/Large screens: inline sidebar with collapse toggle */}
      {showDesktopSidebar && (
        <aside className={`landing-page__sidebar landing-page__sidebar--desktop${sidebarCollapsed ? ' landing-page__sidebar--collapsed' : ''}`}>
          {sidebarCollapsed && (
            <span className="landing-page__expand-toggle" onClick={toggleDesktopSidebar} role="button" aria-label="Expand sidebar">
              <Icon name="angle-right" />
            </span>
          )}
          <div className={sidebarCollapsed ? 'landing-page__sidebar-content--hidden' : undefined}>
            <QuickLinksPanel onCollapse={toggleDesktopSidebar} />
          </div>
        </aside>
      )}

      <main className="landing-page__main">
        <WelcomeBanner />
        <QuickActionsSection />
        <RecentsFeaturedSection />
        <Grid gridDefinition={[{ colspan: { default: 12, s: 6 } }, { colspan: { default: 12, s: 6 } }]}>
          <DueThisWeekSection />
          <TestimonialsSection />
        </Grid>
        <FooterQuote />
      </main>
    </div>
  );
};

export default LandingPage;
